// storybook-check-ignore
import React from 'react';
import { RefreshOfferCta } from 'sellDirect/components/offer/RefreshOfferCta';
import { getHubSSVAUploadRoute, getHubORVAScheduleRoute } from 'shared/helpers/routes';
import { PreClosingOffer, useOfferContext } from 'sellDirect/OfferContext';
import {
  SellDirectAgentPreSignTimelineSteps,
  SellDirectPreSignTimelineItems,
  SellDirectPreSignTimelineSteps,
} from 'shared/helpers/constants';
import { TimelineCta } from 'sellDirect/components/TimelineCta';
import { MarginProps } from '@opendoor/bricks/system/theme';
import { getSSVAExperimentCTA } from 'sellDirect/helpers/virtualAssessmentQuestions';
import { OdProtosWebData_ORVAState as ORVAState } from '__generated__/globalTypes';
import { useExperimentationContext } from 'sellDirect/ExperimentationContext';

type SellerCtaProps = {
  offer: PreClosingOffer;
  /** For users in List+Buy Experiment */
  isListBuyExperiment?: boolean;
};

/**
 * Renders seller / seller agent's CTA on the sell page
 * if expired, show refresh offer button
 * if user is not agent, and ORVA has been scheduled or VA is completed, show nothing
 * if user is agent, and SSVA has been completed, show nothing
 * else, show timeline CTA for the step "show us the / your home"
 */
export const SellerCta: React.FC<SellerCtaProps & MarginProps> = ({ offer, ...marginProps }) => {
  const {
    shouldSeeAgentExperience,
    experimentationData: { shouldSeeSsvaNooExperience },
  } = useOfferContext();
  const {
    assertions: { isnonOwnerOccupiedTreatment },
  } = useExperimentationContext();

  const {
    ssvaData: { ssvaState },
    orvaData: { orvaState },
    isExpired,
    occupancyAtVa,
  } = offer;

  if (isExpired) {
    return <RefreshOfferCta {...marginProps} />;
  }

  const hasCompletedVA = orvaState === ORVAState.ORVA_COMPLETED || ssvaState === 'completed';

  const sellerFinishedVAStep =
    !shouldSeeAgentExperience &&
    !shouldSeeSsvaNooExperience &&
    (orvaState === ORVAState.ORVA_SCHEDULED || hasCompletedVA);
  const agentFinishedVAStep = shouldSeeAgentExperience && hasCompletedVA;
  const ssvaTreatmentFinishedVAStep = shouldSeeSsvaNooExperience && hasCompletedVA;

  if (sellerFinishedVAStep || agentFinishedVAStep || ssvaTreatmentFinishedVAStep) {
    return null;
  }

  let timelineStepInfo = {
    steps: SellDirectPreSignTimelineSteps,
    currentStepName: SellDirectPreSignTimelineItems.SHOW_US_YOUR_HOME,
    buttonText: SellDirectPreSignTimelineItems.SHOW_US_YOUR_HOME,
    href: getHubORVAScheduleRoute(),
  };

  if (shouldSeeAgentExperience) {
    timelineStepInfo = {
      steps: SellDirectAgentPreSignTimelineSteps,
      currentStepName: SellDirectPreSignTimelineItems.SHOW_US_THE_HOME,
      buttonText: SellDirectPreSignTimelineItems.SHOW_US_THE_HOME,
      href: getHubSSVAUploadRoute(),
    };
  }

  if (isnonOwnerOccupiedTreatment) {
    timelineStepInfo = {
      ...timelineStepInfo,
      steps: SellDirectAgentPreSignTimelineSteps,
      currentStepName: SellDirectPreSignTimelineItems.SHOW_US_THE_HOME,
      buttonText: SellDirectPreSignTimelineItems.SHOW_US_THE_HOME,
    };
  }

  // CTA to display for users in the SSVA Targeting Experiment
  if (shouldSeeSsvaNooExperience) {
    const virtualAssessmentCTA = getSSVAExperimentCTA(
      occupancyAtVa,
      shouldSeeAgentExperience,
      orvaState === ORVAState.ORVA_SCHEDULED,
      ssvaState === 'in_progress',
    );
    // If virtualAssessmentCTA is null, that means there is no CTA to show the user
    // e.g. ORVA is scheduled and no SSVA is in progress
    if (!virtualAssessmentCTA) {
      return null;
    }
    return (
      <TimelineCta
        {...marginProps}
        analyticsId={virtualAssessmentCTA.analyticsId ?? 'show-us-your-home-seller-cta'}
        title="Next up"
        {...timelineStepInfo}
        href={virtualAssessmentCTA.path}
      />
    );
  }

  return (
    <TimelineCta
      {...marginProps}
      analyticsId="show-us-your-home-seller-cta"
      title="Next up"
      {...timelineStepInfo}
    />
  );
};
